import React, { useState, useEffect, useCallback } from 'react';
import cardService from '../services/cardService';
import insertSetService from '../services/insertSetService';
import userService from '../services/userService';
import slugService from '../services/slugService';
import CardItem from '../components/CardItem';
import PageGrid from '../components/PageGrid';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import SidebarImage from '../components/Sidebar/SidebarImage';
import UserContactCard from '../components/UserContactCard';
import UserCollectionLink from '../components/UserCollectionLink';
import LoadingSpinner from '../components/LoadingSpinner';
import usePageTitle from '../utils/usePageTitle';
import { getUserCollectionInsertSetBreadcrumbs } from '../utils/breadcrumbUtils';
import { filterCards } from '../utils/filters';
import './ProductPage.css';

const UserCollectionInsertSetPage = ({ username, productSlug, insertSetSlug }) => {
  const [insertSet, setInsertSet] = useState(null);
  const [product, setProduct] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch insert set, product and user data on initial load
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user data first
        if (username) {
          const user = await userService.getUserByUsername(username);
          setUserData(user);
        }

        // Extract ID from insertSetSlug
        if (insertSetSlug) {
          const parsedSlug = slugService.parseSlugWithId(insertSetSlug);
          if (!parsedSlug.id) {
            throw new Error('Could not extract ID from insert set slug');
          }
          
          // Fetch insert set data
          const insertSetData = await insertSetService.getInsertSetById(parsedSlug.id);
          setInsertSet(insertSetData);
          
          // Fetch product data if we have a product ID
          if (insertSetData.productId) {
            const productData = await insertSetService.getProductForInsertSet(insertSetData);
            setProduct(productData);
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError(error.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [username, insertSetSlug]);
  
  // Function to fetch cards for a specific page
  const fetchCards = useCallback(async (page, limit) => {
    if (!insertSet || !userData) {
      return {
        items: [],
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
      };
    }
    
    try {
      // Use the getUserCollectionCardsInInsertSetByPage method to get only 
      // the cards the user has in their collection
      const response = await cardService.getUserCollectionCardsInInsertSetByPage(userData.id, insertSet, page, limit);
      
      return {
        items: response.cards,
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalCards
      };
    } catch (error) {
      console.error('Error fetching user collection cards:', error);
      throw error;
    }
  }, [insertSet, userData]);
  
  
  // Function to render each card
  const renderCard = useCallback((card, index) => {
    // Guard clause - if we don't have user data, we shouldn't render anything
    if (!userData) return null;
    
    // Create a custom URL for a card in a user's collection
    const cardUrl = slugService.getUserCollectionCardUrl(card, insertSet, product, userData.username);
    
    return (
      <CardItem 
        card={card}
        showImage={true}
        index={index}
        inventory={card.inventory || null} // Use inventory data from card
        inWishlist={card.inWishlist || false} // Use wishlist data from card
        insertSet={insertSet}
        product={product}
        cardUrl={cardUrl}
      />
    );
  }, [insertSet, product, userData]);
  
  // Prepare the title including insert set and product info
  const title = insertSet && product
    ? `${insertSet.name} - ${product.name} (${product.year})`
    : '';
    
  // Set the page title
  usePageTitle(insertSet && product && userData ? [`${userData.name}'s Collection`, insertSet.name, `${product.name} (${product.year})`] : '');

  // Back button URL - go back to product page
  const backUrl = product && username 
    ? slugService.getUserCollectionProductUrl(product, username)
    : `/@${username}/collection`;
  
  // Render the sidebar content with product and insert set details
  const renderSidebar = useCallback(() => {
    if (!product || !insertSet || !userData) return null;
    
    return (
      <Sidebar title={<UserCollectionLink user={userData} />}>
        <SidebarImage
          src={product.imageUrl}
          alt={product.name}
        />
        
        <SidebarSection title="Product Details">
          <SidebarRow
            label="Product"
            value={product.name}
          />
          <SidebarRow
            label="Year"
            value={product.year}
          />
          {product.publisher && (
            <SidebarRow
              label="Publisher"
              value={product.publisher}
            />
          )}
        </SidebarSection>
        
        <SidebarSection title="Insert Set Details">
          <SidebarRow
            label="Set Name"
            value={insertSet.name}
          />
          {insertSet.description && (
            <SidebarRow
              label="Description"
              value={insertSet.description}
            />
          )}
          {insertSet.odds && (
            <SidebarRow
              label="Pack Odds"
              value={insertSet.odds}
            />
          )}
          {insertSet.totalCards && (
            <SidebarRow
              label="Total Cards"
              value={insertSet.totalCards}
            />
          )}
        </SidebarSection>
        
        {/* User Contact Information Card */}
        <UserContactCard user={userData} />
      </Sidebar>
    );
  }, [product, insertSet, userData]);
  
  // Show loading spinner while data is being loaded
  if (loading) {
    return <LoadingSpinner />;
  }
  
  // Show error message if there was an error loading the data
  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Collection</h2>
        <p>{error}</p>
      </div>
    );
  }
  
  if(!insertSet || !product || !userData) {
    return <></>;
  }
  
  // Generate breadcrumb items for user collection insert set page
  const breadcrumbItems = getUserCollectionInsertSetBreadcrumbs(userData, product, insertSet);

  return (
    <PageGrid
      title={title}
      fetchItems={fetchCards}
      filterFunction={filterCards}
      renderItem={renderCard}
      renderSidebar={renderSidebar}
      placeholderText={`Filter ${userData.username}'s cards by name or number...`}
      emptyStateMessage={`No cards found in ${userData.name}'s collection for this insert set`}
      itemsPerPage={100}
      backButton={{
        url: backUrl,
        label: "Back"
      }}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default UserCollectionInsertSetPage;